import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Phone, Mail, MessageSquare, Sparkles, Loader2,
  User, Calendar, PhoneIncoming, PhoneOutgoing, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  call:     { label: 'Appel',    icon: Phone,          color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  email:    { label: 'Email',    icon: Mail,            color: 'bg-violet-500/10 text-violet-600 border-violet-500/20' },
  whatsapp: { label: 'WhatsApp', icon: MessageSquare,   color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
  sms:      { label: 'SMS',      icon: MessageSquare,   color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
};

function MemoryItem({ memory }) {
  const cfg = TYPE_CONFIG[memory.type] || TYPE_CONFIG.call;
  const Icon = cfg.icon;
  const ts = memory.timestamp ? new Date(memory.timestamp) : null;

  return (
    <div className="flex gap-4 py-4 border-b last:border-b-0" data-testid={`timeline-item-${memory.id}`}>
      {/* Timeline dot */}
      <div className="flex flex-col items-center pt-1">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cfg.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="w-px flex-1 bg-border mt-2" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={`text-[11px] ${cfg.color}`}>
            {cfg.label}
          </Badge>
          {memory.clone_name && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <User className="w-3 h-3" /> {memory.clone_name}
            </span>
          )}
          {ts && (
            <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto">
              <Calendar className="w-3 h-3" />
              {ts.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
              {' '}
              {ts.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <p className="text-sm mt-1.5 leading-relaxed">{memory.summary}</p>
        {memory.contact_name && (
          <p className="text-xs text-muted-foreground mt-1">
            Contact : {memory.contact_name}
          </p>
        )}
      </div>
    </div>
  );
}

export default function DossierDetailPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dRes, tRes] = await Promise.all([
        axios.get(`${API}/dossiers/${id}`, { headers }),
        axios.get(`${API}/dossiers/${id}/timeline`, { headers }),
      ]);
      setDossier(dRes.data);
      setTimeline(tRes.data.memories || []);
    } catch (e) {
      toast.error('Erreur de chargement du dossier');
      navigate('/dossiers');
    } finally {
      setLoading(false);
    }
  };

  const handleSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await axios.post(`${API}/dossiers/${id}/summary`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data.summary);
    } catch {
      toast.error('Erreur lors de la generation du resume');
    } finally {
      setSummaryLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Dossier">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (!dossier) return null;

  return (
    <DashboardLayout title={dossier.title}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dossiers')} data-testid="back-to-dossiers">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold" data-testid="dossier-title">{dossier.title}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              {dossier.primary_contact_name && (
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" /> {dossier.primary_contact_name}
                </span>
              )}
              <span className="flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> {dossier.memory_count} interactions
              </span>
              {dossier.auto_created && (
                <Badge variant="outline" className="text-[10px] bg-sky-500/10 text-sky-600 border-sky-500/20">
                  Auto
                </Badge>
              )}
              <span className="text-xs">
                Cree le {new Date(dossier.created_at).toLocaleDateString('fr-FR')}
              </span>
            </div>
          </div>
        </div>

        {dossier.description && (
          <p className="text-sm text-muted-foreground pl-12">{dossier.description}</p>
        )}

        {/* AI Summary */}
        <Card data-testid="dossier-summary-card">
          <CardContent className="py-4">
            {summary ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" /> Resume IA
                  </h3>
                  <Button variant="ghost" size="sm" onClick={handleSummary} disabled={summaryLoading}
                    className="text-xs" data-testid="refresh-summary-btn">
                    Actualiser
                  </Button>
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="summary-content">
                  {summary}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Generez un resume intelligent de ce dossier base sur toutes les interactions
                </p>
                <Button onClick={handleSummary} disabled={summaryLoading} size="sm"
                  className="gap-2 shrink-0" data-testid="generate-summary-btn">
                  {summaryLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Generer le resume
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        <div data-testid="dossier-timeline">
          <h3 className="text-sm font-medium text-muted-foreground mb-4 pl-1">
            Historique des interactions ({timeline.length})
          </h3>
          {timeline.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                Aucune interaction pour ce dossier.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-2">
                {timeline.map(m => (
                  <MemoryItem key={m.id} memory={m} />
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
